import React, { useEffect, useRef } from 'react';

import { defineMessages, FormattedMessage } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Text, xcss } from '@atlaskit/primitives';
import {
	isSearchAtlasPartial,
	isSearchCompassPartial,
	isSearchConfluencePartial,
	isSearchJiraPartial,
	isSearchResultGraphEntity,
	isSearchResultJiraIssue,
	isSearchResultJiraProject,
	isSearchTrelloPartial,
	isSlackMessage,
	mapJiraIssuePartial,
	mapJiraProjectPartial,
	type SearchPageNode,
} from '@atlassian/dynamic-cards-search-client';

import { SearchResultSkeleton } from '../../../../common/ui/search-result-skeleton';
import { ErrorBoundary } from '../../../../controllers/error-boundary';
import {
	useFetchFromCursor,
	useIsLoadingMore,
	usePageEndCursor,
	useResults,
	useSearchActions,
	useSelectedProduct,
} from '../../../../controllers/store';
import OAuthContainerWithErrorBoundary from './oauth-container';
import { AtlasSearchResult } from './product-results/atlas';
import { CompassSearchResult } from './product-results/compass';
import { ConfluenceSearchResult } from './product-results/confluence';
import { JiraSearchResult } from './product-results/jira';
import { ThirdPartySearchResult } from './product-results/third-party';
import { SlackSearchResult } from './product-results/third-party/slack-search-result';
import { TrelloSearchResult } from './product-results/trello';

const resultsListGridAreaStyles = xcss({
	gridArea: 'results-list',
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.200',
});

const noMoreResultsStyles = xcss({
	marginBottom: 'space.400',
});

// How many search results to show before the OAuth banner
const OAUTH_BANNER_CUTOFF = 4;

export const i18n: {
	resultsCount: {
		defaultMessage: string;
		description: string;
		id: string;
	};
} = defineMessages({
	resultsCount: {
		id: 'search-result.result.count',
		defaultMessage: '{count, plural, one {Showing {count} result} other {Showing {count} results}}',
		description: 'Use to describe a channel when the name is not available',
	},
});

type ResultRecordProps = {
	index: number;
	node: SearchPageNode;
};

// TODO: we should monitor for unsupported result types, right now we're failing silently and fall back to null
const ResultRecord = ({ node, index }: ResultRecordProps) => {
	return (
		<ErrorBoundary key={node.id} fallback={null}>
			{isSearchConfluencePartial(node) ? (
				<ConfluenceSearchResult partial={node} position={index} />
			) : isSearchResultJiraIssue(node) ? (
				<JiraSearchResult partial={mapJiraIssuePartial(node)} position={index} />
			) : isSearchResultJiraProject(node) ? (
				<JiraSearchResult partial={mapJiraProjectPartial(node)} position={index} />
			) : isSearchJiraPartial(node) ? (
				<JiraSearchResult partial={node} position={index} />
			) : isSearchAtlasPartial(node) ? (
				<AtlasSearchResult partial={node} position={index} />
			) : isSearchCompassPartial(node) ? (
				<CompassSearchResult partial={node} position={index} />
			) : isSearchTrelloPartial(node) ? (
				<TrelloSearchResult partial={node} position={index} />
			) : isSlackMessage(node) ? (
				<SlackSearchResult partial={node} position={index} />
			) : isSearchResultGraphEntity(node) ? (
				// Leave this one last
				<ThirdPartySearchResult partial={node} position={index} />
			) : null}
		</ErrorBoundary>
	);
};

export const Results = (): React.JSX.Element => {
	const [results] = useResults();
	const { setFetchFromCursor } = useSearchActions();
	const currentCursor = useFetchFromCursor();
	const nextFetchCursor = usePageEndCursor();
	const isLoadingMore = useIsLoadingMore();
	const hasNextPage = nextFetchCursor && currentCursor !== nextFetchCursor;
	const [selectedProduct] = useSelectedProduct();
	const lastResultRef = useRef<HTMLDivElement | null>(null);
	const paginationEnabled = false;

	useEffect(() => {
		if (!paginationEnabled) {
			return;
		}
		const nextPageObserver = new IntersectionObserver((entries) => {
			if (entries[0].isIntersecting) {
				if (nextFetchCursor && nextFetchCursor !== currentCursor) {
					setFetchFromCursor(nextFetchCursor);
				}
			}
		}, {});
		if (lastResultRef.current) {
			nextPageObserver.observe(lastResultRef.current);
		}
	}, [currentCursor, nextFetchCursor, paginationEnabled, results, setFetchFromCursor]);

	if (!results) {
		return <></>;
	}

	const initialResults = results.edges.slice(0, OAUTH_BANNER_CUTOFF);
	const remainingResults = results.edges.slice(OAUTH_BANNER_CUTOFF);
	const resultsCount = paginationEnabled ? results.totalCount : results.edges.length;

	return (
		<Box
			xcss={[resultsListGridAreaStyles, noMoreResultsStyles]}
			role="list"
			testId="search-page-results-list"
		>
			{resultsCount && (
				<Text color="color.text.subtle">
					<FormattedMessage
						{...i18n.resultsCount}
						values={{
							count: resultsCount,
						}}
					/>
				</Text>
			)}
			{initialResults.map(({ node }, index) => (
				<ResultRecord node={node} index={index} />
			))}
			{!selectedProduct && <OAuthContainerWithErrorBoundary />}
			{remainingResults.length > 0 &&
				remainingResults.map(({ node }, index) => (
					<ResultRecord node={node} index={index + OAUTH_BANNER_CUTOFF} />
				))}
			{paginationEnabled && (hasNextPage || isLoadingMore) && (
				<Box ref={lastResultRef}>
					<SearchResultSkeleton />
				</Box>
			)}
		</Box>
	);
};
